import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkBookingModal = ({ isOpen, onClose, selectedShifts, shifts, onConfirmBooking }) => {
  const [conflicts, setConflicts] = useState([]);
  const [overtimeWarnings, setOvertimeWarnings] = useState([]);
  const [totalHours, setTotalHours] = useState(0);

  useEffect(() => {
    if (isOpen && selectedShifts?.length > 0) {
      checkConflictsAndOvertime();
    }
  }, [isOpen, selectedShifts]);

  const checkConflictsAndOvertime = () => {
    const selectedShiftData = shifts?.filter(shift => selectedShifts?.includes(shift?.id));
    
    // Calculate total hours
    const total = selectedShiftData?.reduce((sum, shift) => sum + shift?.duration, 0);
    setTotalHours(total);

    // Check for time conflicts
    const conflictList = [];
    const warnings = [];

    selectedShiftData?.sort((a, b) => new Date(a.date) - new Date(b.date));

    for (let i = 0; i < selectedShiftData?.length - 1; i++) {
      const current = selectedShiftData?.[i];
      const next = selectedShiftData?.[i + 1];

      // Check if shifts are on the same day
      if (current?.date === next?.date) {
        const currentEnd = new Date(`${current.date}T${current.endTime}`);
        const nextStart = new Date(`${next.date}T${next.startTime}`);

        if (currentEnd > nextStart) {
          conflictList?.push({
            type: 'overlap',
            message: `Überschneidung zwischen Schichten am ${new Date(current.date)?.toLocaleDateString('de-DE')}`
          });
        }
      }
    }

    // Check for overtime warnings
    const weeklyHours = {};
    selectedShiftData?.forEach(shift => {
      const date = new Date(shift.date);
      const weekStart = new Date(date.setDate(date.getDate() - date.getDay()));
      const weekKey = weekStart?.toISOString()?.split('T')?.[0];
      
      if (!weeklyHours?.[weekKey]) {
        weeklyHours[weekKey] = 0;
      }
      weeklyHours[weekKey] += shift?.duration;
    });

    Object.entries(weeklyHours)?.forEach(([week, hours]) => {
      if (hours > 40) {
        warnings?.push({
          type: 'overtime',
          message: `Überstunden in der Woche vom ${new Date(week)?.toLocaleDateString('de-DE')}: ${hours - 40}h`
        });
      }
    });

    setConflicts(conflictList);
    setOvertimeWarnings(warnings);
  };

  const getSelectedShiftData = () => {
    return shifts?.filter(shift => selectedShifts?.includes(shift?.id));
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`)?.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getDepartmentLabel = (department) => {
    const labels = {
      'customer-service': 'Kundendienst',
      'operations': 'Betrieb',
      'manufacturing': 'Fertigung',
      'administration': 'Verwaltung',
      'maintenance': 'Wartung',
      'security': 'Sicherheit'
    };
    return labels?.[department] || department;
  };

  const hasBlockingConflicts = conflicts?.some(c => c?.type === 'overlap');
  const canProceed = !hasBlockingConflicts;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            Mehrere Schichten buchen
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
          />
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Summary */}
            <div className="bg-muted rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{selectedShifts?.length}</div>
                  <div className="text-sm text-muted-foreground">Schichten</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{totalHours}h</div>
                  <div className="text-sm text-muted-foreground">Gesamtstunden</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {Math.ceil(totalHours * 15)}€
                  </div>
                  <div className="text-sm text-muted-foreground">Geschätzte Vergütung</div>
                </div>
              </div>
            </div>

            {/* Conflicts */}
            {conflicts?.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-foreground mb-3">
                  <Icon name="AlertTriangle" size={20} className="inline mr-2 text-error" />
                  Konflikte erkannt
                </h3>
                <div className="space-y-2">
                  {conflicts?.map((conflict, index) => (
                    <div key={index} className="flex items-start space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                      <Icon name="AlertTriangle" size={16} className="text-error mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-error">Zeitkonflikt</p>
                        <p className="text-sm text-error/80">{conflict?.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overtime Warnings */}
            {overtimeWarnings?.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-foreground mb-3">
                  <Icon name="Clock" size={20} className="inline mr-2 text-warning" />
                  Überstunden-Hinweise
                </h3>
                <div className="space-y-2">
                  {overtimeWarnings?.map((warning, index) => (
                    <div key={index} className="flex items-start space-x-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                      <Icon name="Clock" size={16} className="text-warning mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-warning">Überstunden-Warnung</p>
                        <p className="text-sm text-warning/80">{warning?.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Shifts List */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-3">
                Ausgewählte Schichten ({selectedShifts?.length})
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {getSelectedShiftData()?.map((shift) => (
                  <div key={shift?.id} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div>
                        <div className="font-medium text-foreground">
                          {formatDate(shift?.date)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {formatTime(shift?.startTime)} - {formatTime(shift?.endTime)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {getDepartmentLabel(shift?.department)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {shift?.duration}h
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {shift?.isPriority && (
                        <Icon name="Star" size={16} className="text-amber-500" />
                      )}
                      {shift?.hasCrossTraining && (
                        <Icon name="GraduationCap" size={16} className="text-emerald-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Confirmation */}
            {canProceed && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                  <div>
                    <h4 className="font-medium text-success mb-1">Bereit zur Buchung</h4>
                    <p className="text-sm text-success/80">
                      Alle ausgewählten Schichten können gebucht werden. 
                      {overtimeWarnings?.length > 0 && ' Bitte beachten Sie die Überstunden-Hinweise.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <Button
            variant="ghost"
            onClick={onClose}
          >
            Abbrechen
          </Button>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-muted-foreground">
              {selectedShifts?.length} Schichten, {totalHours}h gesamt
            </span>
            <Button
              variant="default"
              onClick={() => onConfirmBooking(selectedShifts)}
              disabled={!canProceed}
              iconName="Calendar"
              iconPosition="left"
            >
              {canProceed ? 'Alle buchen' : 'Konflikte lösen'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkBookingModal;